import { LogLevel } from '@environment/enums/log-level.enum';
import { startTransaction } from '@sentry/browser';
import { hasChromeRuntime } from '@shared/extension';
import { LOG_STORAGE_PREFIX } from '@shared/extension/utils/stored-logs';
import { Blacklist } from '@shared/http/classes/blacklist';
import { RequestResponseEvent } from '@shared/http/interfaces/request-event.interface';
import { SentryEventType } from '../enums';
import { SentryErrorEvent, SentryLogEvent } from '../interfaces';
import { Environment } from '@environment/interfaces';

let environment: Environment;

export function initSentryEvents(env: Environment): void {
  captureSentryMessage('Initializing Sentry events logger with log level:', env?.sentryLogLevel);
  environment = env;
}

export function postEventMessage(event: RequestResponseEvent): void {
  event = setRequestUrlToAbsoluteUrl(event);
  if (Blacklist.isBlacklisted(event)) {
    return;
  }
  windowPostMessage(SentryEventType.PostBackend, event);
}

export function sendLogToSentry(event: SentryLogEvent): void {
  if (shouldSendLogsToSentry(event.level)) {
    windowPostMessage(SentryEventType.Log, event);
  }
}

export function sendErrorToSentry(event: SentryErrorEvent): void {
  windowPostMessage(SentryEventType.Error, event);
}

function saveLogLocally(log: { message: string; data: any; level: LogLevel; tags?: { [key: string]: string } }): void {
  logMessageToConsole(log.level, log.message);
  const timestamp = Date.now();
  const uniqueId = generateUniqueId();
  const key = `${LOG_STORAGE_PREFIX}::${uniqueId}::${timestamp}`;
  const enhancedLog = {
    ...log,
    data: {
      ...log.data,
      lastError: chrome.runtime.lastError?.message,
    },
    tags: {
      ...log.tags,
      hasChromeRuntime: hasChromeRuntime(),
      networkStatus: navigator.onLine ? 'online' : 'offline',
      source: 'local-logger',
    },
  };
  chrome.storage.local.set({ [key]: enhancedLog }, () => {
    if (chrome.runtime.lastError) {
      console.error('Failed to save log locally:', chrome.runtime.lastError);
    }
  });
}

function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 15);
}

export function captureSentryMessage(
  message: string,
  data = {},
  level = LogLevel.Info,
  tags?: { [key: string]: string }
): void {
  try {
    if (chrome.runtime?.id && navigator?.onLine) {
      originalCaptureSentryMessage(message, data, level, tags);
    } else {
      saveLogLocally({ message, data, level, tags });
    }
  } catch (e) {
    console.error('Failed to capture Sentry message:', e);
  }
}

/**
 * Capture single message to Sentry
 * @param message
 * @param data
 * @param level
 * @param tags
 */
export function originalCaptureSentryMessage(
  message: string,
  data = {} as any,
  level = LogLevel.Info,
  tags?: { [key: string]: string | number }
): void {
  if (shouldSendLogsToSentry(level)) {
    logMessageToConsole(level, message);
    const transaction = startTransaction({
      name: message,
    });
    const span = transaction.startChild({
      op: message,
      data: data,
      tags,
    });
    span.finish();
    transaction.finish();
  }
}

function logMessageToConsole(level: LogLevel, message: string): void {
  switch (level) {
    case LogLevel.Debug:
      console.debug(message);
      break;
    case LogLevel.Info:
      console.info(message);
      break;
    case LogLevel.Warning:
      console.warn(message);
      break;
  }
}

function shouldSendLogsToSentry(level: LogLevel): boolean {
  const severityOrder = [LogLevel.Trace, LogLevel.Debug, LogLevel.Info, LogLevel.Warning, LogLevel.Error];
  const logLevel = environment?.sentryLogLevel || LogLevel.Info;
  return severityOrder.indexOf(level) >= severityOrder.indexOf(logLevel);
}

function windowPostMessage(type: SentryEventType, event: unknown): void {
  try {
    let content: unknown;
    if (type === SentryEventType.Error) {
      // Do not serialize Errors because they are not JSON serializable
      content = event;
    } else {
      // Stringify and parse to remove any unserializable from the
      content = JSON.parse(JSON.stringify(event));
    }
    window.postMessage({ type, content }, window.location.origin);
  } catch (e) {
    if (type === SentryEventType.Error) {
      console.error('Error while logging an error in Sentry.', e);
      console.error('Error attempted to be logged.', event);
    } else {
      windowPostMessage(SentryEventType.Error, e);
    }
  }
}

function setRequestUrlToAbsoluteUrl(event: RequestResponseEvent): RequestResponseEvent {
  if (!event.request.url.startsWith('http') && !event.request.url.startsWith('https')) {
    event.request.url = window.location.origin + event.request.url;
  }
  return event;
}
